import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import ManualTOC from "./ManualTOC";
import ManualStructureEditor from "./ManualStructureEditor";
import StructureActionsPanel from "./StructureActionsPanel";
import ManualAuthoringHeader from "./ManualAuthoringHeader";
import BlockCanvas from "./BlockCanvas";
import BlockPalette from "./BlockPalette";
import ReviewCommentsPanel from "../reviewComments/ReviewCommentsPanel";
import {
  fetchManualJson,
  updateManualContent,
} from "../../apiServices/manualsApi";
import { useAuthoringStore } from "../../store/UseAuthorStore";
import { useUserStore } from "../../store/userStore";
import { useManualAuthoringStore } from "../../store/manualAuthoringStore";
import { useReviewCommentStore } from "../../store/useReviewCommentStore";
import "./ManualStructurePage.css";

export default function ManualStructurePage() {
  const { manualId } = useParams();
  const location = useLocation();

  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeEditBlockId, setActiveEditBlockId] = useState<string | null>(null);
  const [activePaletteTab, setActivePaletteTab] = useState<
    "content" | "comments"
  >("content");
  const [commentFocusBlockId, setCommentFocusBlockId] = useState<string | null>(
    null
  );
  const [commentAutoStartToken, setCommentAutoStartToken] = useState(0);

  const contentIdRef = useRef<number>(0);
  const editSnapshotRef = useRef<any>(null);

  const user = useUserStore((s) => s.user);
  const accessToken = useUserStore((s) => s.accessToken);
  const masterAssembly = useAuthoringStore((s) => s.masterAssembly);
  const setReviewCommentContext = useReviewCommentStore((s) => s.setContext);
  const loadReviewComments = useReviewCommentStore((s) => s.loadComments);

  const loadManual = useManualAuthoringStore((s) => s.loadManual);
  const saveManual = useManualAuthoringStore((s) => s.saveManual);
  const presentManual = useManualAuthoringStore((s) => s.presentManual);
  const selectSection = useManualAuthoringStore((s) => s.selectSection);
  const selectedSectionId = useManualAuthoringStore(
    (s) => s.selectedSectionId
  );
  const selectedBlockId = useManualAuthoringStore((s) => s.selectedBlockId);

  const basePath = process.env.REACT_APP_BASE_NAME || "";
  const oemFromBase = basePath.split("/")[1] || "";
  const oemFromUser = user?.oemId ? `Oem${user.oemId}` : "";
  const oem = oemFromBase || oemFromUser || "Oem1";
  const parsedManualId = useMemo(() => {
    const id = Number(manualId);
    return Number.isNaN(id) ? 0 : id;
  }, [manualId]);
  const queryMasterAssemblyId = useMemo(() => {
    const raw = new URLSearchParams(location.search).get("masterAssemblyId");
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  }, [location.search]);
  const pdfLanguageCode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const lang = String(
      params.get("lang") || params.get("translationLang") || ""
    )
      .trim()
      .toLowerCase();
    return lang || "";
  }, [location.search]);
  const resolvedMasterAssemblyId =
    (queryMasterAssemblyId > 0 ? queryMasterAssemblyId : 0) ||
    (masterAssembly?.masterAssemblyId ?? 0);

  useEffect(() => {
    if (!resolvedMasterAssemblyId) return;
    setReviewCommentContext(oem, resolvedMasterAssemblyId);
    void loadReviewComments();
  }, [
    loadReviewComments,
    oem,
    resolvedMasterAssemblyId,
    setReviewCommentContext,
  ]);

  useEffect(() => {
    if (!selectedSectionId) {
      setCommentFocusBlockId(null);
      return;
    }
    if (selectedBlockId) {
      setCommentFocusBlockId(null);
    }
  }, [selectedBlockId, selectedSectionId]);

  /* ---------------- LOAD ---------------- */

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      const id = Number(manualId);
      if (!id || Number.isNaN(id)) {
        loadManual({ sections: [] });
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await fetchManualJson(oem, id);
        if (!mounted) return;

        contentIdRef.current = result.contentId || 0;
        loadManual(result.manual ?? { sections: [] });
      } catch {
        if (!mounted) return;

        contentIdRef.current = 0;
        loadManual({ sections: [] });
        setError("No existing content. Starting new hierarchy.");
      } finally {
        mounted && setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [manualId, oem, loadManual]);

  /* ---------------- EDIT MODE ---------------- */

  const enterEditMode = () => {
    editSnapshotRef.current = JSON.parse(
      JSON.stringify(useManualAuthoringStore.getState().presentManual)
    );
    setEditMode(true);
  };

  const exitEditMode = () => {
    setEditMode(false);
    setActiveEditBlockId(null);
    selectSection(null);
  };

  const cancelEdit = () => {
    if (editSnapshotRef.current) {
      loadManual(editSnapshotRef.current);
    }
    exitEditMode();
  };

  /* ---------------- SAVE ---------------- */

  const saveHierarchy = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const manual = saveManual();

      await updateManualContent(oem, {
        contentId: contentIdRef.current,
        jsonContent: JSON.stringify(manual),
        isActive: true,
        modifiedOn: new Date().toISOString(),
      });

      if (editMode) {
        exitEditMode();
      } else {
        setActiveEditBlockId(null);
      }
    } catch (e: any) {
      alert(e?.message || "Failed to save hierarchy");
    } finally {
      setIsSaving(false);
    }
  };

  const exportPdf = async () => {
    setActiveEditBlockId(null);
    const id = Number(manualId);
    if (!id || Number.isNaN(id)) {
      alert("Invalid manual id for PDF export.");
      return;
    }

    const baseUrl = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");
    if (!baseUrl) {
      alert("API base URL is not configured.");
      return;
    }

    const query = pdfLanguageCode
      ? `?lang=${encodeURIComponent(pdfLanguageCode)}`
      : "";
    const endpoint = `${baseUrl}/PdfTest/manual/${oem.toLowerCase()}/${id}${query}`;
    const headers: HeadersInit = {};
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    try {
      const response = await fetch(endpoint, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        let errorMessage = `Failed to generate PDF (${response.status}).`;
        try {
          const body = await response.json();
          if (body?.message) {
            errorMessage = body.message;
          }
        } catch {
          // ignore JSON parse error
        }
        alert(errorMessage);
        return;
      }

      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const match = disposition.match(/filename\*?=(?:UTF-8''|\"?)([^\";]+)/i);
      const filename = match
        ? decodeURIComponent(match[1].replace(/\"/g, ""))
        : `manual-${id}.pdf`;

      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err: any) {
      alert(err?.message || "Failed to generate PDF.");
    }
  };

  /* ---------------- UI ---------------- */

  const sections = presentManual?.sections ?? [];
  const openCommentsForBlock = (blockId: string, startAddComposer = false) => {
    setCommentFocusBlockId(blockId);
    setActivePaletteTab("comments");
    if (startAddComposer) {
      setCommentAutoStartToken(Date.now());
    }
  };

  return (
    <div className="container-fluid manual-structure-page">
      <div className="manual-authoring-header__wrap">
        <ManualAuthoringHeader
          onSave={saveHierarchy}
          onExportPdf={exportPdf}
          isSaving={isSaving}
        />
      </div>

      <div className="manual-structure-page__body">
        <div className="row g-3 h-100">

          {/* TOC */}
          <div className="col-2 border-end p-2 manual-structure-page__toc-col">
            <div className="manual-structure-page__toc-scroll">
              <ManualTOC onEdit={enterEditMode} />
            </div>
          </div>

          {editMode ? (
            /* -------- TOC EDIT MODE -------- */
          <>
              <div className="col-8 border-end p-3 manual-structure-page__main-col">
                <div className="manual-structure-page__main-scroll">
                  <ManualStructureEditor />
                </div>
              </div>

              <div className="col-2 p-3 manual-structure-page__side-col">
                <div className="manual-structure-page__side-scroll">
                  <StructureActionsPanel
                    onSave={saveHierarchy}
                    onCancel={cancelEdit}
                    isSaving={isSaving}
                  />
                </div>
              </div>
            </>
          ) : selectedSectionId ? (
            /* -------- CONTENT MODE -------- */
            <>
              <div className="col-8 border-end p-3 manual-structure-page__main-col">
                <div className="manual-structure-page__main-scroll">
                  <div className="d-flex justify-content-center">
                    <BlockCanvas
                      activeEditBlockId={activeEditBlockId}
                      setActiveEditBlockId={setActiveEditBlockId}
                      manualId={parsedManualId}
                      onOpenCommentsForBlock={openCommentsForBlock}
                    />
                  </div>
                </div>
              </div>

              <div className="col-2 p-3 manual-structure-page__side-col">
                <div className="manual-structure-page__side-scroll">
                  <div
                    className="btn-group w-100 mb-3"
                    role="tablist"
                    aria-label="Right panel tabs"
                  >
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        activePaletteTab === "content"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() => setActivePaletteTab("content")}
                    >
                      Palette
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        activePaletteTab === "comments"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() => setActivePaletteTab("comments")}
                    >
                      Comments
                    </button>
                  </div>

                  {activePaletteTab === "content" ? (
                    <BlockPalette onSave={saveHierarchy} isSaving={isSaving} />
                  ) : (
                    <ReviewCommentsPanel
                      domain="manual"
                      masterAssemblyId={resolvedMasterAssemblyId}
                      manualId={parsedManualId > 0 ? parsedManualId : undefined}
                      sectionId={selectedSectionId}
                      blockId={commentFocusBlockId || selectedBlockId || undefined}
                      autoStartAddToken={commentAutoStartToken || undefined}
                    />
                  )}
                </div>
              </div>
            </>
          ) : (
            /* -------- VIEW MODE -------- */
            <div className="col-8 p-3 manual-structure-page__main-col">
              <div className="manual-structure-page__main-scroll">
                {loading && <div className="alert alert-info">Loading…</div>}
                {error && <div className="alert alert-warning">{error}</div>}

                <div className="border rounded bg-white p-4">
                  <h5>Manual Structure</h5>
                  <hr />

                  {sections.length === 0 ? (
                    <div className="text-muted">
                      No sections yet. Click <b>Edit TOC</b>.
                    </div>
                  ) : (
                    <div>
                      {sections.map((section) => (
                      <div key={section.id} className="mb-3">
                      <div className="fw-semibold">{section.title}</div>
                        {section.subSections?.map((sub) => (
                        <div key={sub.id} className="ms-3 text-muted">
                        {sub.title}
                        </div>
                        ))}
                      </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
